import React, { useState, useEffect, useMemo } from "react";
import "./CurrentTimeBar.css";
import DateDisplay from "./DateDisplay";
import { ScreenData } from "../../types/ScreenTypes";
import {
  getCurrentTimeInTimezone,
  getCurrentIslamicDate,
} from "../../utils/timeUtils";

function clampScale(
  value: number | undefined,
  minimum: number,
  maximum: number,
  fallback: number,
): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(maximum, Math.max(minimum, parsed));
}

function resolveTimeFontFamily(fontFamily: string | undefined): string {
  switch ((fontFamily || "").toLowerCase()) {
    case "open-sans":
      return '"Open Sans", Arial, Helvetica, sans-serif';
    case "share-tech-mono":
      return '"Share Tech Mono", "Courier New", monospace';
    case "lcd-modern":
      return '"LCD Modern", monospace';
    case "lcd-modern-bold":
      return '"LCD Modern Bold", monospace';
    case "lcd-classic":
      return '"LCD Classic", monospace';
    case "lcd-modern-italic":
    default:
      return '"LCD Modern Italic", monospace';
  }
}

function resolveDateFontFamily(fontFamily: string | undefined): string {
  switch ((fontFamily || "").toLowerCase()) {
    case "serif":
      return 'Georgia, "Times New Roman", serif';
    case "share-tech-mono":
      return '"Share Tech Mono", "Courier New", monospace';
    case "open-sans":
    default:
      return '"Open Sans", Arial, Helvetica, sans-serif';
  }
}

function normalizeDateAlignment(
  alignment: string | undefined,
): "left" | "center" | "right" {
  const normalized = (alignment || "").toLowerCase();
  switch (normalized) {
    case "left":
    case "right":
      return normalized;
    case "center":
    default:
      return "center";
  }
}

interface CurrentTimeBarProps {
  screenData: ScreenData;
  prayerTimes?: any;
  tomorrowPrayerTimes?: any;
  overrideIslamicDate?: string; // Add this
  overrideGregorianDate?: string; // Add this
}

const CurrentTimeBar: React.FC<CurrentTimeBarProps> = ({
  screenData,
  prayerTimes,
  tomorrowPrayerTimes,
  overrideIslamicDate,
  overrideGregorianDate,
}) => {
  // Detect orientation
  const [isLandscape, setIsLandscape] = useState(
    window.innerWidth > window.innerHeight,
  );

  // Current time state
  const [currentTime, setCurrentTime] = useState(() =>
    getCurrentTimeInTimezone(screenData?.timeZone),
  );

  // Update time every second
  useEffect(() => {
    function updateTime() {
      setCurrentTime(getCurrentTimeInTimezone(screenData?.timeZone));
    }
    updateTime();
    const timer = setInterval(updateTime, 1000);
    return () => clearInterval(timer);
  }, [screenData?.timeZone]);

  // Listen for orientation changes
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Use override if provided, otherwise calculate
  const formattedIslamicDate = useMemo(() => {
    if (overrideIslamicDate) return overrideIslamicDate;

    return getCurrentIslamicDate(
      prayerTimes || null,
      tomorrowPrayerTimes || null,
      screenData?.changeIslamicDateAtMaghrib ?? true,
      screenData?.timeZone || "UTC",
      screenData?.hijriDateFormat,
      screenData?.customizedNames,
    );
  }, [
    overrideIslamicDate,
    prayerTimes,
    tomorrowPrayerTimes,
    screenData?.changeIslamicDateAtMaghrib,
    screenData?.timeZone,
    screenData?.hijriDateFormat,
    screenData?.customizedNames,
  ]);

  // Create a date string that only changes at midnight (for dependency tracking)
  // This ensures Gregorian date updates at midnight without recalculating every second
  const currentDateString = useMemo(() => {
    return `${currentTime.getFullYear()}-${currentTime.getMonth()}-${currentTime.getDate()}`;
  }, [currentTime]);

  // Calculate Gregorian date or use override
  const formattedGregorianDate = useMemo(() => {
    if (overrideGregorianDate) return overrideGregorianDate;

    const today = currentTime;
    const options: Intl.DateTimeFormatOptions = {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    };
    return today.toLocaleDateString("en-US", options);
  }, [overrideGregorianDate, currentDateString, currentTime]); // Only recalculates when date changes

  // Format time display with seconds
  const timeDisplay = useMemo(() => {
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes();
    const seconds = currentTime.getSeconds();

    if (screenData?.use12HourFormat) {
      const hour12 = hours % 12 || 12;
      const period = hours >= 12 ? "pm" : "am";
      return {
        main: `${hour12}:${minutes.toString().padStart(2, "0")}`,
        seconds: seconds.toString().padStart(2, "0"),
        period: null,
      };
    } else {
      return {
        main: `${hours.toString().padStart(2, "0")}:${minutes
          .toString()
          .padStart(2, "0")}`,
        seconds: seconds.toString().padStart(2, "0"),
        period: null,
      };
    }
  }, [currentTime, screenData?.use12HourFormat]);

  const showSeconds = screenData?.showTimeSeconds ?? true;
  const timeTextColor = screenData?.timeTextColor || "#FFFFFF";
  const timeFontFamily = useMemo(
    () => resolveTimeFontFamily(screenData?.timeFontFamily),
    [screenData?.timeFontFamily],
  );

  const timeFontScalePercent = useMemo(() => {
    return clampScale(screenData?.timeFontScalePercent, 70, 1200, 100);
  }, [screenData?.timeFontScalePercent]);

  const timeFontWeight = useMemo(() => {
    const parsed = Number(screenData?.timeFontWeight);
    if (!Number.isFinite(parsed)) return 700;
    return Math.min(900, Math.max(400, parsed));
  }, [screenData?.timeFontWeight]);

  const timeMainStyle = useMemo(
    () => ({
      color: timeTextColor,
      fontWeight: timeFontWeight,
      fontFamily: timeFontFamily,
      fontSize: `calc(var(--time-main-size, 4vw) * ${timeFontScalePercent / 100})`,
    }),
    [timeFontFamily, timeTextColor, timeFontWeight, timeFontScalePercent],
  );

  const timeSecondsStyle = useMemo(
    () => ({
      color: timeTextColor,
      fontFamily: timeFontFamily,
      fontWeight: Math.max(400, Math.min(900, timeFontWeight - 100)),
    }),
    [timeFontFamily, timeTextColor, timeFontWeight],
  );

  const periodStyle = useMemo(
    () => ({
      color: timeTextColor,
      fontFamily: timeFontFamily,
    }),
    [timeFontFamily, timeTextColor],
  );

  const gregorianDateStyle = useMemo(
    () => ({
      color: screenData?.gregorianDateTextColor || "#FFFFFF",
      fontWeight: clampScale(
        screenData?.gregorianDateFontWeight,
        400,
        900,
        500,
      ),
      fontFamily: resolveDateFontFamily(screenData?.gregorianDateFontFamily),
      fontSize: `calc(var(--time-date-size, 2vw) * ${
        clampScale(screenData?.gregorianDateFontScalePercent, 70, 1200, 100) /
        100
      })`,
    }),
    [
      screenData?.gregorianDateFontFamily,
      screenData?.gregorianDateFontScalePercent,
      screenData?.gregorianDateFontWeight,
      screenData?.gregorianDateTextColor,
    ],
  );

  const hijriDateStyle = useMemo(
    () => ({
      color: screenData?.hijriDateTextColor || "#FFFFFF",
      fontWeight: clampScale(screenData?.hijriDateFontWeight, 400, 900, 500),
      fontFamily: resolveDateFontFamily(screenData?.hijriDateFontFamily),
      fontSize: `calc(var(--time-date-size, 2vw) * ${
        clampScale(screenData?.hijriDateFontScalePercent, 70, 1200, 100) / 100
      })`,
    }),
    [
      screenData?.hijriDateFontFamily,
      screenData?.hijriDateFontScalePercent,
      screenData?.hijriDateFontWeight,
      screenData?.hijriDateTextColor,
    ],
  );

  const gregorianDateAlignment = useMemo(
    () => normalizeDateAlignment(screenData?.gregorianDateAlignment),
    [screenData?.gregorianDateAlignment],
  );

  const hijriDateAlignment = useMemo(
    () => normalizeDateAlignment(screenData?.hijriDateAlignment),
    [screenData?.hijriDateAlignment],
  );

  return (
    <div className={`time-section ${isLandscape ? "landscape" : "portrait"}`}>
      {/* Time Display */}
      <div className="current-time">
        <span className="time-main" style={timeMainStyle}>
          {timeDisplay.main}
          {showSeconds && (
            <span className="time-seconds" style={timeSecondsStyle}>
              :{timeDisplay.seconds}
            </span>
          )}
        </span>
        {timeDisplay.period && (
          <span className="period" style={periodStyle}>
            {timeDisplay.period}
          </span>
        )}
      </div>

      {/* Date Display - Different layout for landscape vs portrait */}
      {isLandscape ? (
        <div className="date-row-landscape">
          <div
            className={`date-half-landscape date-half-gregorian align-${gregorianDateAlignment}`}
          >
            <span
              className="gregorian-date-landscape"
              style={gregorianDateStyle}
            >
              {formattedGregorianDate}
            </span>
          </div>
          <span className="date-separator-landscape">|</span>
          <div
            className={`date-half-landscape date-half-hijri align-${hijriDateAlignment}`}
          >
            <span className="hijri-date-landscape" style={hijriDateStyle}>
              {formattedIslamicDate}
            </span>
          </div>
        </div>
      ) : (
        <div className="date-display-portrait">
          <DateDisplay
            gregorianDate={formattedGregorianDate}
            hijriDate={formattedIslamicDate}
            layout="stacked"
            gregorianAlignment={gregorianDateAlignment}
            hijriAlignment={hijriDateAlignment}
            gregorianStyle={{
              color: screenData?.gregorianDateTextColor || "#FFFFFF",
              fontWeight: clampScale(
                screenData?.gregorianDateFontWeight,
                400,
                900,
                500,
              ),
              fontFamily: resolveDateFontFamily(
                screenData?.gregorianDateFontFamily,
              ),
              fontSize: `calc(var(--date-stacked-size, 1.6rem) * ${
                clampScale(
                  screenData?.gregorianDateFontScalePercent,
                  70,
                  1200,
                  100,
                ) / 100
              })`,
            }}
            hijriStyle={{
              color: screenData?.hijriDateTextColor || "#FFFFFF",
              fontWeight: clampScale(
                screenData?.hijriDateFontWeight,
                400,
                900,
                500,
              ),
              fontFamily: resolveDateFontFamily(
                screenData?.hijriDateFontFamily,
              ),
              fontSize: `calc(var(--date-stacked-size, 1.6rem) * ${
                clampScale(
                  screenData?.hijriDateFontScalePercent,
                  70,
                  1200,
                  100,
                ) / 100
              })`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CurrentTimeBar;
